import axios, { AxiosError, AxiosResponse } from "axios"
import { useMemo } from "react"
import { SetUserRequestedDeletionDocument } from "../generated/graphql"
import { useStorage } from "../hooks/useStorage"
import { ENDPOINTS } from "./endpoints"
import { ErrorResponse, LoginResponse, SafetyChecklistMetadata } from "./types"

export const useApi = () => {
  const { jwt } = useStorage()

  const options = useMemo(
    () => ({
      headers: {
        Authorization: `Bearer ${jwt}`,
        "vira-authn-key": process.env.REACT_APP_VIRA_AUTHN_APIM_KEY || "",
      },
    }),
    [jwt]
  )

  return useMemo(
    () => ({
      login: async (
        email: string,
        password: string
      ): Promise<AxiosResponse<LoginResponse>> => {
        return await axios
          .post(
            ENDPOINTS.LOGIN,
            { email, password },
            {
              headers: {
                "Content-Type": "application/json",
                "vira-authn-key":
                  process.env.REACT_APP_VIRA_AUTHN_APIM_KEY || "",
              },
            }
          )
          .catch((error) => error.response)
      },

      deleteAccountLogin: async (
        email: string,
        password: string
      ): Promise<AxiosResponse<LoginResponse>> => {
        return await axios
          .post(ENDPOINTS.DELETE_ACCOUNT_LOGIN, { email, password })
          .catch((error) => error.response)
      },

      deactivateAccount: async (
        userId: string
      ): Promise<AxiosResponse<any>> => {
        const jwt = localStorage.getItem("jwt") // Retrieve the JWT token from localStorage
        const options = {
          headers: {
            Authorization: `Bearer ${jwt}`,
          },
        }
        const response = await axios.post(
          ENDPOINTS.DEACTIVATE_MOBILE_USER,
          { user_id: userId },
          options
        )
        try {
          return response
        } catch (error) {
          const axiosError = error as AxiosError
          console.log("Deactivation Error:", axiosError.message) // Log the error response
          return response
        }
      },

      initiateAccountAndDataRemoval: async (
        userId: string
      ): Promise<AxiosResponse<any>> => {
        const jwt = localStorage.getItem("jwt") // Retrieve the JWT token from localStorage
        const options = {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
        const body = {
          query: SetUserRequestedDeletionDocument,
          variables: { userId: userId },
        }
        console.log("Body:", body)
        try {
          const response = await axios.post(ENDPOINTS.GRAPHQL, body, options)
          console.log("Account and Data Removal Request Response:", response)
          return response
        } catch (error) {
          const axiosError = error as AxiosError
          console.log(
            "Account and Data Removal Request Error:",
            axiosError.message
          )
          throw axiosError // Rethrowing the error might be useful for error handling
        }
      },

      logout: async () => await axios.post(ENDPOINTS.LOGOUT, {}, options),

      register: async (
        email: string,
        password: string,
        user_data: {
          first_name: string
          last_name: string
          notification_time: Date
        }
      ): Promise<AxiosResponse> => {
        return await axios
          .post(
            ENDPOINTS.REGISTER,
            {
              email,
              password,
              user_data,
              register_options: {
                default_role: "practitioner",
                allowed_roles: ["practitioner", "web_role"], // to support 'Supervisor', 'Care Team' and additional roles in the future
              },
            },
            {
              headers: {
                "Content-Type": "application/json",
                "vira-authn-key":
                  process.env.REACT_APP_VIRA_AUTHN_APIM_KEY || "",
              },
            }
          )
          .catch((error) => error.response)
      },

      changeEmail: async (email: string) => {
        return await axios.post(
          ENDPOINTS.CHANGE_EMAIL,
          { new_email: email },
          options
        )
      },

      changePassword: async (
        oldPassword: string,
        newPassword: string,
        email: string
      ) => {
        return await axios
          .post(
            ENDPOINTS.CHANGE_PASSWORD,
            { old_password: oldPassword, new_password: newPassword, email },
            options
          )
          .catch((error) => error.response)
      },

      requestPasswordReset: async (
        email: string
      ): Promise<AxiosResponse<ErrorResponse | undefined>> => {
        return await axios
          .post(ENDPOINTS.REQUEST_PASSWORD_RESET, { email }, options)
          .catch((error) => error.response)
      },

      resetPassword: async (ticket: string, password: string) => {
        return await axios.post(
          ENDPOINTS.RESET_PASSWORD,
          { ticket, new_password: password },
          {
            headers: {
              "Content-Type": "application/json",
              "vira-authn-key": process.env.REACT_APP_VIRA_AUTHN_APIM_KEY || "",
            },
          }
        )
      },

      emailStaffInvite: async (
        to: string,
        firstName: string,
        lastName: string
      ) => {
        return await axios.post(
          ENDPOINTS.EMAIL_STAFF_INVITE,
          {
            to,
            firstName,
            lastName,
          },
          options
        )
      },

      getMostRecentAppVersions: async () => {
        return (await axios.get(
          ENDPOINTS.GET_CURRENT_APP_VERSIONS,
          options
        )) as AxiosResponse<{ android: string; apple: string }>
      },

      scheduleConnectPractitionerNotification: async (code: string) => {
        return await axios.post(
          ENDPOINTS.SCHEDULE_CONNECT_PRACTITIONER,
          {
            code,
          },
          options
        )
      },

      scheduleDisconnectPractitionerNotification: async (code: string) => {
        return await axios.post(
          ENDPOINTS.SCHEDULE_DISCONNECT_PRACTITIONER,
          {
            code,
          },
          options
        )
      },

      sendSmsVerificationCode: async (to: string): Promise<AxiosResponse> => {
        return await axios.post(
          ENDPOINTS.SEND_SMS_VERIFICATION_CODE,
          {
            to,
          },
          options
        )
      },

      verifySmsCode: async (
        to: string,
        code: string
      ): Promise<AxiosResponse> => {
        return await axios.post(
          ENDPOINTS.CHECK_VERIFICATION_CODE,
          {
            to,
            code,
          },
          options
        )
      },

      emailHomeSafetyChecklist: async (
        userId: string,
        recipientEmail: string,
        metadata: SafetyChecklistMetadata
      ): Promise<AxiosResponse> => {
        return await axios.post(
          ENDPOINTS.EMAIL_HOME_SAFETY_CHECKLIST,
          {
            userId,
            recipientEmail,
            metadata,
          },
          options
        )
      },

      downloadHomeSafetyChecklist: async (): Promise<AxiosResponse> => {
        return await axios.get(ENDPOINTS.DOWNLOAD_HOME_SAFETY_CHECKLIST, {
          ...options,
          responseType: "blob",
        })
      },

      // to support ViraPro Client Mobile Account Creation
      registerClient: async (
        email: string,
        password: string,
        user_data: {
          first_name: string
          last_name: string
          notification_time: Date
        }
      ): Promise<AxiosResponse> => {
        return await axios
          .post(
            ENDPOINTS.REGISTER,
            {
              email,
              password,
              user_data,
              register_options: {
                default_role: "user",
                allowed_roles: ["user"],
              },
            },
            {
              headers: {
                "Content-Type": "application/json",
                "vira-authn-key":
                  process.env.REACT_APP_VIRA_AUTHN_APIM_KEY || "",
              },
            }
          )
          .catch((error) => error.response)
      },

      assignDefaultUserFeatureFlags: async (
        invite_code: string,
        is_ehr: boolean,
        headers?: any
      ) => {
        return await axios.post(
          ENDPOINTS.ASSIGN_DEFAULT_USER_FEATURE_FLAGS,
          { invite_code, is_ehr },
          headers || options
        )
      },

      createInviteCode: async (
        phoneNumber: string,
        email?: string
      ): Promise<AxiosResponse> => {
        return await axios.post(
          ENDPOINTS.CREATE_INVITE_CODE,
          {
            phoneNumber,
            email,
          },
          options
        )
      },
    }),
    [options]
  )
}
